/**
 * ExplorationTracker — detects repetitive file reads and search patterns
 * during planning-mode queries to nudge agents past analysis paralysis.
 *
 * Follows the CostTracker pattern: stateful accumulator with read-only getters.
 * Instantiated per-query in QueryBridge.buildHost() for discovery/auto-planning
 * modes only. Building/review modes set this to null on QueryHost.
 */

export type NudgeLevel = 1 | 2;

export interface StalenessSignal {
  level: NudgeLevel;
  message: string;
}

// Base thresholds (before complexity scaling)
const FILE_REREAD_NUDGE_L1 = 3;
const FILE_REREAD_NUDGE_L2 = 5;
const SEARCH_REPEAT_NUDGE_L1 = 3;
const SEARCH_REPEAT_NUDGE_L2 = 5;
const RATIO_MIN_READS_L1 = 10;
const RATIO_THRESHOLD_L1 = 0.4;
const RATIO_MIN_READS_L2 = 15;
const RATIO_THRESHOLD_L2 = 0.3;

function normalizePath(filePath: string): string {
  // Strip leading ./ for consistent tracking
  let normalized = filePath.replace(/^\.\//, "");
  // Collapse consecutive slashes
  normalized = normalized.replace(/\/+/g, "/");
  return normalized;
}

export class ExplorationTracker {
  private fileReadCounts = new Map<string, number>();
  private searchCounts = new Map<string, number>();
  private totalReads = 0;
  private highestNudgeEmitted: NudgeLevel | 0 = 0;
  private readonly complexityMultiplier: number;

  constructor(isParentTask: boolean) {
    this.complexityMultiplier = isParentTask ? 1.5 : 1.0;
  }

  /** Record a file read and return a staleness signal if thresholds are crossed. */
  recordFileRead(filePath: string): StalenessSignal | null {
    const normalized = normalizePath(filePath);
    const count = (this.fileReadCounts.get(normalized) ?? 0) + 1;
    this.fileReadCounts.set(normalized, count);
    this.totalReads++;

    // Check file re-read thresholds (higher level first for priority)
    return this.checkFileRereadSignal(normalized, count) ?? this.checkExplorationRatio();
  }

  /** Record a search pattern and return a staleness signal if thresholds are crossed. */
  recordSearch(pattern: string): StalenessSignal | null {
    const count = (this.searchCounts.get(pattern) ?? 0) + 1;
    this.searchCounts.set(pattern, count);

    return this.checkSearchRepeatSignal(pattern, count);
  }

  private scaledThreshold(base: number): number {
    return Math.ceil(base * this.complexityMultiplier);
  }

  private checkFileRereadSignal(filePath: string, count: number): StalenessSignal | null {
    const l2 = this.scaledThreshold(FILE_REREAD_NUDGE_L2);
    const l1 = this.scaledThreshold(FILE_REREAD_NUDGE_L1);

    if (count >= l2 && this.highestNudgeEmitted < 2) {
      this.highestNudgeEmitted = 2;
      return {
        level: 2,
        message:
          `⚠️ Exploration warning: You've read "${filePath}" ${count} times, suggesting analysis paralysis. ` +
          `Write your plan with the context you have and call ExitPlanMode. ` +
          `If something specific is blocking you, describe the blocker in your plan.`,
      };
    }

    if (count >= l1 && this.highestNudgeEmitted < 1) {
      this.highestNudgeEmitted = 1;
      return {
        level: 1,
        message:
          `Exploration note: You've read "${filePath}" ${count} times. ` +
          `Consider whether you have enough context to start writing your plan. ` +
          `If you're still exploring, try looking at different files.`,
      };
    }

    return null;
  }

  private checkSearchRepeatSignal(pattern: string, count: number): StalenessSignal | null {
    const l2 = this.scaledThreshold(SEARCH_REPEAT_NUDGE_L2);
    const l1 = this.scaledThreshold(SEARCH_REPEAT_NUDGE_L1);

    // Truncate pattern for display
    const displayPattern = pattern.length > 60 ? pattern.slice(0, 57) + "..." : pattern;

    if (count >= l2 && this.highestNudgeEmitted < 2) {
      this.highestNudgeEmitted = 2;
      return {
        level: 2,
        message:
          `⚠️ Exploration warning: You've searched for "${displayPattern}" ${count} times. ` +
          `Repeated searches for the same pattern suggest you may be stuck. ` +
          `Proceed with the information you have.`,
      };
    }

    if (count >= l1 && this.highestNudgeEmitted < 1) {
      this.highestNudgeEmitted = 1;
      return {
        level: 1,
        message:
          `Exploration note: You've searched for "${displayPattern}" ${count} times. ` +
          `If you're not finding what you need, try a different approach or broader pattern.`,
      };
    }

    return null;
  }

  private checkExplorationRatio(): StalenessSignal | null {
    const uniqueReads = this.fileReadCounts.size;
    const ratio = this.totalReads > 0 ? uniqueReads / this.totalReads : 1;

    const minReadsL2 = this.scaledThreshold(RATIO_MIN_READS_L2);
    if (
      this.totalReads >= minReadsL2 &&
      ratio < RATIO_THRESHOLD_L2 &&
      this.highestNudgeEmitted < 2
    ) {
      this.highestNudgeEmitted = 2;
      return {
        level: 2,
        message:
          `⚠️ Exploration warning: Only ${Math.round(ratio * 100)}% of your ${this.totalReads} file reads are unique. ` +
          `This indicates significant re-reading. Write your plan with the context you have.`,
      };
    }

    const minReadsL1 = this.scaledThreshold(RATIO_MIN_READS_L1);
    if (
      this.totalReads >= minReadsL1 &&
      ratio < RATIO_THRESHOLD_L1 &&
      this.highestNudgeEmitted < 1
    ) {
      this.highestNudgeEmitted = 1;
      return {
        level: 1,
        message:
          `Exploration note: ${Math.round(ratio * 100)}% of your ${this.totalReads} file reads are unique. ` +
          `You're re-reading files more than exploring new ones. Consider starting your plan.`,
      };
    }

    return null;
  }
}
